/**
 * Microphones, cameras, speakers — and the playback of everybody else.
 *
 * ── Labels cost a permission ────────────────────────────────────────────────
 * `enumerateDevices()` answers before anything has been granted, but every
 * `label` is `''` — a browser will not tell a page what hardware is attached
 * until the person has allowed some of it. So a device list is asked for
 * twice: once for the shape, then, if the labels are blank and there is
 * anything to name, once more behind a throwaway `getUserMedia`. A settings
 * page that showed "Microphone 1 · Microphone 2" would be a settings page
 * nobody can use.
 *
 * ── Muting sends silence ────────────────────────────────────────────────────
 * `track.enabled = false` keeps the packets flowing with nothing in them.
 * `track.stop()` ends the track, which tears the transceiver down and needs a
 * renegotiation to undo — and the microphone light going out and coming back
 * on every mute is how a person learns not to trust the mute button. Nothing
 * here stops a live track; `session.ts` holds to the same rule.
 *
 * ── Speakers are an element property ────────────────────────────────────────
 * WebRTC has no notion of an output device: the choice belongs to whatever
 * `<audio>` plays the remote track, through `setSinkId`, which Firefox and
 * Safari do not implement. Where it is missing the system default is used and
 * nothing is said — an error about a control that cannot work is worse than
 * the control quietly doing what it always did.
 */

import type { AudioProcessing, DeviceList } from './types';

/** The same three constraints `CallSession.setAudioProcessing` takes, under the
 *  name the media code uses for them. An alias rather than a second interface:
 *  two identical declarations are two things to change and one to forget. */
export type AudioSettings = AudioProcessing;

/** All three on. Each is a setting because each is occasionally wrong — a
 *  musician wants gain control off, a headset user wants echo cancellation off
 *  — and all three are right for everybody else. */
export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true
};

/** 640×360 at 24: a call's camera, not a photograph. The SFU forwards whatever
 *  it is given, so the sender's choice is every receiver's bandwidth. */
export const CAMERA_CONSTRAINTS = { width: 640, height: 360, frameRate: 24 } as const;

export function micConstraints(
  settings: AudioSettings = DEFAULT_AUDIO_SETTINGS,
  deviceId?: string
): MediaStreamConstraints {
  return {
    audio: {
      ...settings,
      ...(deviceId ? { deviceId: { exact: deviceId } } : {})
    },
    video: false
  };
}

export function camConstraints(deviceId?: string): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      width: { ideal: CAMERA_CONSTRAINTS.width },
      height: { ideal: CAMERA_CONSTRAINTS.height },
      frameRate: { ideal: CAMERA_CONSTRAINTS.frameRate },
      ...(deviceId ? { deviceId: { exact: deviceId } } : {})
    }
  };
}

/** The three calls this engine makes into the browser's media stack, named so a
 *  test can stand in for them. */
export interface MediaAccess {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  getDisplayMedia(constraints: DisplayMediaStreamOptions): Promise<MediaStream>;
  enumerateDevices(): Promise<MediaDeviceInfo[]>;
}

/** Read through `navigator` at each call rather than captured: this is built
 *  when a room is opened, which is before anything has been asked for. */
export function browserMedia(): MediaAccess {
  return {
    getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c),
    getDisplayMedia: (c) => navigator.mediaDevices.getDisplayMedia(c),
    enumerateDevices: () => navigator.mediaDevices.enumerateDevices()
  };
}

function split(devices: MediaDeviceInfo[]): DeviceList {
  return {
    mics: devices.filter((d) => d.kind === 'audioinput'),
    cams: devices.filter((d) => d.kind === 'videoinput'),
    speakers: devices.filter((d) => d.kind === 'audiooutput')
  };
}

/** True when there is hardware to name and no name for any of it — the state a
 *  page is in before it has ever asked for a microphone. */
function nameless(devices: MediaDeviceInfo[]): boolean {
  return devices.length > 0 && devices.every((d) => d.label === '');
}

export async function listDevices(media: MediaAccess): Promise<DeviceList> {
  const first = await media.enumerateDevices();
  if (!nameless(first)) return split(first);

  try {
    const stream = await media.getUserMedia(micConstraints());
    for (const track of stream.getTracks()) track.stop();
  } catch {
    // Refused, or no microphone. The nameless list is still the true list of
    // what is attached, and it is what the caller gets.
    return split(first);
  }
  return split(await media.enumerateDevices());
}

/**
 * Everybody else's audio, playing.
 *
 * A remote audio track is not heard until something plays it, and that
 * something is where two features live that have nowhere else to go: deafening
 * (every element muted at once) and the speaker choice (`setSinkId` on each).
 * Keeping one element per participant rather than one mixed stream is what
 * makes a per-person volume possible later without moving any of this.
 */
export interface RemoteAudio {
  attach(id: string, track: MediaStreamTrack): void;
  detach(id: string): void;
  setMuted(muted: boolean): void;
  setSpeaker(deviceId: string): Promise<void>;
  stop(): void;
}

/** `setSinkId` is optional here and not in the DOM's own typing, because
 *  Firefox and Safari do not have it. */
type SinkCapable = Omit<HTMLAudioElement, 'setSinkId'> & {
  setSinkId?: (deviceId: string) => Promise<void>;
};

/** A no-op sink, for anywhere without a document — a test, and the server
 *  render that never happens but would be a crash if it did. */
function silentAudio(): RemoteAudio {
  return {
    attach: () => {},
    detach: () => {},
    setMuted: () => {},
    setSpeaker: async () => {},
    stop: () => {}
  };
}

export function createRemoteAudio(): RemoteAudio {
  if (typeof document === 'undefined') return silentAudio();

  const elements = new Map<string, SinkCapable>();
  let muted = false;
  let speaker = '';

  const apply = async (el: SinkCapable): Promise<void> => {
    el.muted = muted;
    if (speaker && el.setSinkId) await el.setSinkId(speaker).catch(() => {});
  };

  return {
    attach(id, track) {
      elements.get(id)?.remove();
      const el = document.createElement('audio') as SinkCapable;
      el.autoplay = true;
      el.srcObject = new MediaStream([track]);
      elements.set(id, el);
      void apply(el);
      // Never in the document: an `<audio>` plays whether it is in the tree or
      // not, and one that is would be a control somebody's stylesheet has to
      // hide.
      void el.play().catch(() => {});
    },

    detach(id) {
      const el = elements.get(id);
      if (!el) return;
      el.srcObject = null;
      el.remove();
      elements.delete(id);
    },

    setMuted(next) {
      muted = next;
      for (const el of elements.values()) el.muted = next;
    },

    async setSpeaker(deviceId) {
      speaker = deviceId;
      for (const el of elements.values()) {
        if (el.setSinkId) await el.setSinkId(deviceId).catch(() => {});
      }
    },

    stop() {
      for (const id of [...elements.keys()]) this.detach(id);
    }
  };
}
